import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';

const StarIcon = ({ filled, size = 36 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#f59e0b' : 'none'} stroke={filled ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

const RATING_LABELS = ['', '残念だった', 'あまり良くなかった', '普通', '良かった', 'とても良かった'];

const ReviewPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hovered, setHovered] = useState(0);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [alreadyReviewed, setAlreadyReviewed] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get(`projects/${projectId}`),
            api.get(`reviews/by_project/${projectId}`),
        ]).then(([pr, rv]) => {
            setProject(pr.data);
            if (rv.data?.id) setAlreadyReviewed(true);
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, [projectId]);

    const handleSubmit = async () => {
        if (rating === 0) return;
        setSubmitting(true);
        try {
            const customerId = localStorage.getItem('customer_id');
            await api.post('reviews', {
                project_id: Number(projectId),
                contractor_id: project.contractor_id,
                customer_name: project.customer
                ? `${project.customer.last_name || ''}${project.customer.first_name || ''}`.trim() || `顧客 #${customerId}`
                : `顧客 #${customerId}`,
                rating,
                comment: comment.trim() || null,
            });
            setDone(true);
        } catch {
            // 投稿失敗してもダッシュボードへ
            navigate('/customer/dashboard');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    if (done || alreadyReviewed) {
        return (
            <div style={{ minHeight: 'calc(100vh - 64px)', background: '#f5f6f8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ background: '#fff', borderRadius: 12, padding: '48px 40px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <div style={{ marginBottom: 20 }}><CheckCircleIcon /></div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
                        {alreadyReviewed && !done ? 'レビュー投稿済みです' : 'レビューありがとうございます'}
                    </h2>
                    <p style={{ color: '#6b7280', lineHeight: 1.7, margin: '0 0 32px' }}>
                        {alreadyReviewed && !done
                            ? 'このプロジェクトへのレビューはすでに投稿済みです。'
                            : 'いただいたご意見は業者の改善に役立てられます。\nご利用ありがとうございました。'}
                    </p>
                    <button
                        onClick={() => navigate('/customer/dashboard')}
                        style={{ width: '100%', padding: '13px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
                    >
                        ダッシュボードへ
                    </button>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const displayRating = hovered || rating;
    const contractor = project?.contractor;

    return (
        <div style={{ minHeight: 'calc(100vh - 64px)', background: '#f5f6f8' }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .star-btn:hover { transform: scale(1.15); }
                .star-btn { transition: transform 0.1s; }
                .review-textarea:focus { border-color: #005c53 !important; outline: none; }
                .skip-btn:hover { color: #374151 !important; }
            `}</style>

            {/* ヘッダー */}
            <div style={{ background: '#005c53', padding: '28px 0' }}>
                <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
                    <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
                        工事はいかがでしたか？
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: '0.875rem' }}>
                        {contractor?.company_name || '業者'} へのレビューをお願いします
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 20px 64px' }}>

                {/* 評価カード */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '36px 32px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, textAlign: 'center' }}>

                    {/* 業者名 */}
                    {contractor && (
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 28px' }}>
                            {contractor.company_name}
                        </p>
                    )}

                    {/* 星評価 */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
                        {[1, 2, 3, 4, 5].map(n => (
                            <button
                                key={n}
                                className="star-btn"
                                type="button"
                                onMouseEnter={() => setHovered(n)}
                                onMouseLeave={() => setHovered(0)}
                                onClick={() => setRating(n)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                            >
                                <StarIcon filled={n <= displayRating} size={40} />
                            </button>
                        ))}
                    </div>

                    {/* 評価ラベル */}
                    <p style={{ fontSize: '0.9rem', fontWeight: 600, color: displayRating ? '#92400e' : '#9ca3af', margin: '0 0 28px', minHeight: '1.4em' }}>
                        {displayRating ? RATING_LABELS[displayRating] : '星をクリックして評価してください'}
                    </p>

                    {/* コメント */}
                    <div style={{ textAlign: 'left' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 8 }}>
                            コメント <span style={{ color: '#9ca3af', fontWeight: 400 }}>（任意）</span>
                        </label>
                        <textarea
                            className="review-textarea"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            rows={4}
                            placeholder="工事の仕上がり・業者の対応など、感想をお聞かせください"
                            style={{
                                width: '100%', padding: '12px 14px',
                                border: '1px solid #e0e4ea', borderRadius: 6,
                                fontSize: '0.9rem', color: '#1a1a2e',
                                resize: 'none', boxSizing: 'border-box',
                                lineHeight: 1.6, transition: 'border-color 0.15s',
                            }}
                        />
                    </div>
                </div>

                {/* ボタン群 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={rating === 0 || submitting}
                        style={{
                            width: '100%', padding: '14px',
                            background: rating === 0 ? '#d1d5db' : '#005c53',
                            color: '#fff', border: 'none', borderRadius: 8,
                            fontWeight: 700, fontSize: '1rem',
                            cursor: rating === 0 ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                        }}
                    >
                        {submitting ? '投稿中...' : 'レビューを投稿する'}
                    </button>

                    <button
                        type="button"
                        className="skip-btn"
                        onClick={() => navigate('/customer/dashboard')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.875rem', padding: '8px', transition: 'color 0.15s' }}
                    >
                        今回はスキップする
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReviewPage;
